import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "../../../Components/Table/Table";
import { formatTradePrice } from "../../../helper/formatTradePrice";
import { isoToJalali } from "../../../helper/isoToJalali";
import { API_BASE_URL } from "../../../config/api";
import { ButtonLoader, Spinner } from "../../../Components/AsyncState";
import Pagination from "../../../Components/Pagination/Pagination";

const PARTICIPATION_BASE_PATH = `${API_BASE_URL}/api/v0/payment-payout/admin`;
const USERS_ENDPOINT = `${API_BASE_URL}/api/v0/auth/admin/users/all/`;
const NETWORKS_ENDPOINT = `${PARTICIPATION_BASE_PATH}/networks/`;

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function getErrorMessageFromPayload(payload, fallbackText) {
  if (!payload) return fallbackText;
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  const firstArrayValue = Object.values(payload).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (firstArrayValue && typeof firstArrayValue[0] === "string") {
    return firstArrayValue[0];
  }

  return fallbackText;
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return isoToJalali(value);
  } catch {
    return String(value);
  }
}

function formatCount(value) {
  const number = Number(value);
  if (Number.isFinite(number)) return number.toLocaleString();
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function formatPackageBreakdown(value) {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.join(" | ") || "-";
  if (typeof value === "object") {
    const parts = Object.entries(value).map(([key, val]) => `${key}: ${val}`);
    return parts.length > 0 ? parts.join(" | ") : "-";
  }
  return String(value);
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = "انتخاب کنید",
  disabled = false,
}) {
  return (
    <div className="w-full flex flex-col gap-2">
      <label className="text-sm text-zi-text-light/60 dark:text-zi-text-dark/60">
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="w-full rounded-lg py-2 px-2.5 border border-zi-border-light dark:border-zi-border-dark text-zi-text-light dark:text-zi-text-dark bg-transparent"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = "" }) {
  return (
    <div className="w-full flex flex-col gap-2">
      <label className="text-sm text-zi-text-light/60 dark:text-zi-text-dark/60">
        {label}
      </label>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-lg py-2 px-2.5 border border-zi-border-light dark:border-zi-border-dark text-zi-text-light dark:text-zi-text-dark bg-transparent"
      />
    </div>
  );
}

export default function DefinitionTab({ authFetch }) {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersPagination, setUsersPagination] = useState({ page: 1, totalPages: 0 });

  const [networks, setNetworks] = useState([]);
  const [networksLoading, setNetworksLoading] = useState(true);
  const [networksPagination, setNetworksPagination] = useState({ page: 1, count: 0, totalPages: 0 });

  const [selectedNetworkId, setSelectedNetworkId] = useState("");
  const [selectedNetworkDetail, setSelectedNetworkDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [pageError, setPageError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [bloggerUserId, setBloggerUserId] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");

  const [networkOwnerUserId, setNetworkOwnerUserId] = useState("");
  const [networkName, setNetworkName] = useState("");
  const [networkCode, setNetworkCode] = useState("");

  const [assignNetworkId, setAssignNetworkId] = useState("");
  const [assignUserId, setAssignUserId] = useState("");

  const userOptions = useMemo(() => {
    return users.map((user) => {
      const fullName = [user.fn, user.ln].filter(Boolean).join(" ").trim();
      const title = fullName || user.email || `کاربر ${user.id}`;
      const details = [user.email, user.code].filter(Boolean).join(" - ");

      return {
        value: String(user.id),
        label: details ? `${title} (${details})` : title,
      };
    });
  }, [users]);

  const networkOptions = useMemo(() => {
    return networks.map((network) => ({
      value: String(network.id),
      label: `${network.name || "-"} (${network.code || "-"})`,
    }));
  }, [networks]);

  const fetchUsers = useCallback(async (page = 1) => {
    setUsersLoading(true);
    try {
      const response = await authFetch(`${USERS_ENDPOINT}?page=${page}`, { method: "GET" });
      if (!response) return;
      if (!response.ok) {
        throw new Error(`users-fetch-failed-${response.status}`);
      }

      const payload = await response.json();
      const nextUsers = normalizeList(payload);
      setUsers((current) => page === 1 ? nextUsers : [...current, ...nextUsers]);
      setUsersPagination({
        page: payload?.page ?? page,
        totalPages: payload?.total_pages ?? page,
      });
    } catch (error) {
      console.error("Error fetching participation users:", error);
      setPageError("بارگذاری کاربران با خطا مواجه شد.");
    } finally {
      setUsersLoading(false);
    }
  }, [authFetch]);

  const fetchNetworks = useCallback(async (page = 1) => {
    setNetworksLoading(true);
    try {
      const response = await authFetch(`${NETWORKS_ENDPOINT}?page=${page}&page_size=100`, { method: "GET" });
      if (!response) return;
      if (!response.ok) {
        throw new Error(`networks-fetch-failed-${response.status}`);
      }

      const payload = await response.json();
      const nextNetworks = normalizeList(payload);
      setNetworks((current) => page === 1 ? nextNetworks : [...current, ...nextNetworks]);
      setNetworksPagination({
        page: payload.page ?? page,
        count: payload.count ?? nextNetworks.length,
        totalPages: payload.total_pages ?? 0,
      });
    } catch (error) {
      console.error("Error fetching participation networks:", error);
      setPageError("بارگذاری شبکه‌ها با خطا مواجه شد.");
    } finally {
      setNetworksLoading(false);
    }
  }, [authFetch]);

  const fetchNetworkDetail = useCallback(
    async (networkId) => {
      if (!networkId) {
        setSelectedNetworkDetail(null);
        return;
      }

      setDetailLoading(true);
      try {
        const response = await authFetch(`${NETWORKS_ENDPOINT}${networkId}/`, {
          method: "GET",
        });

        if (!response) return;
        if (!response.ok) {
          throw new Error(`network-detail-fetch-failed-${response.status}`);
        }

        const payload = await response.json();
        setSelectedNetworkDetail(payload);
      } catch (error) {
        console.error("Error fetching participation network detail:", error);
        setPageError("بارگذاری جزئیات شبکه با خطا مواجه شد.");
        setSelectedNetworkDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [authFetch],
  );

  useEffect(() => {
    setPageError("");
    fetchUsers(1);
    fetchNetworks();
  }, [fetchUsers, fetchNetworks]);

  useEffect(() => {
    if (networks.length === 0) {
      setSelectedNetworkId("");
      setAssignNetworkId("");
      setSelectedNetworkDetail(null);
      return;
    }

    const hasSelected = networks.some(
      (network) => String(network.id) === String(selectedNetworkId),
    );
    if (!hasSelected) {
      setSelectedNetworkId(String(networks[0].id));
    }

    const hasAssignSelected = networks.some(
      (network) => String(network.id) === String(assignNetworkId),
    );
    if (!hasAssignSelected) {
      setAssignNetworkId(String(networks[0].id));
    }
  }, [networks, selectedNetworkId, assignNetworkId]);

  useEffect(() => {
    fetchNetworkDetail(selectedNetworkId);
  }, [fetchNetworkDetail, selectedNetworkId]);

  const runAction = useCallback(async (operation) => {
    setActionError("");
    setActionSuccess("");
    setActionLoading(true);
    try {
      await operation();
    } finally {
      setActionLoading(false);
    }
  }, []);

  const parseId = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  const handleCreateProfile = (kind) => {
    const userIdValue = kind === "blogger" ? bloggerUserId : ownerUserId;
    const userId = parseId(userIdValue);
    if (!userId) {
      setActionError("ابتدا یک کاربر معتبر انتخاب کنید.");
      return;
    }

    const endpoint =
      kind === "blogger"
        ? `${PARTICIPATION_BASE_PATH}/participants/bloggers/`
        : `${PARTICIPATION_BASE_PATH}/participants/network-owners/`;

    runAction(async () => {
      const response = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      });

      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(payload, "ثبت پروفایل با خطا مواجه شد."),
        );
        return;
      }

      setActionSuccess(
        kind === "blogger"
          ? "پروفایل بلاگر با موفقیت ایجاد شد."
          : "پروفایل مالک شبکه با موفقیت ایجاد شد.",
      );
    });
  };

  const handleDeleteProfile = (kind) => {
    const userIdValue = kind === "blogger" ? bloggerUserId : ownerUserId;
    const userId = parseId(userIdValue);
    if (!userId) {
      setActionError("ابتدا یک کاربر معتبر انتخاب کنید.");
      return;
    }

    const endpoint =
      kind === "blogger"
        ? `${PARTICIPATION_BASE_PATH}/participants/bloggers/${userId}/`
        : `${PARTICIPATION_BASE_PATH}/participants/network-owners/${userId}/`;

    runAction(async () => {
      const response = await authFetch(endpoint, { method: "DELETE" });
      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(payload, "حذف پروفایل با خطا مواجه شد."),
        );
        return;
      }

      setActionSuccess(
        kind === "blogger"
          ? "پروفایل بلاگر با موفقیت حذف شد."
          : "پروفایل مالک شبکه با موفقیت حذف شد.",
      );
    });
  };

  const handleCreateNetwork = () => {
    const ownerId = parseId(networkOwnerUserId);
    if (!ownerId || !networkName.trim() || !networkCode.trim()) {
      setActionError("فیلدهای ایجاد شبکه را کامل کنید.");
      return;
    }

    runAction(async () => {
      const response = await authFetch(NETWORKS_ENDPOINT, {
        method: "POST",
        body: JSON.stringify({
          owner_user_id: ownerId,
          name: networkName.trim(),
          code: networkCode.trim(),
        }),
      });

      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(payload, "ایجاد شبکه با خطا مواجه شد."),
        );
        return;
      }

      const payload = await response.json().catch(() => null);
      setActionSuccess("شبکه با موفقیت ایجاد شد.");
      setNetworkName("");
      setNetworkCode("");

      await fetchNetworks();
      if (payload?.id) {
        setSelectedNetworkId(String(payload.id));
        setAssignNetworkId(String(payload.id));
      }
    });
  };

  const handleAssignBlogger = () => {
    const networkId = parseId(assignNetworkId);
    const userId = parseId(assignUserId);
    if (!networkId || !userId) {
      setActionError("برای افزودن عضو، شبکه و کاربر را انتخاب کنید.");
      return;
    }

    runAction(async () => {
      const response = await authFetch(
        `${NETWORKS_ENDPOINT}${networkId}/bloggers/`,
        {
          method: "POST",
          body: JSON.stringify({ user_id: userId }),
        },
      );

      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(
            payload,
            "افزودن بلاگر به شبکه با خطا مواجه شد.",
          ),
        );
        return;
      }

      const payload = await response.json().catch(() => null);
      const createdText = payload?.auto_blogger_created
        ? " (پروفایل بلاگر به‌صورت خودکار ایجاد شد)"
        : "";
      setActionSuccess(`عضو با موفقیت به شبکه اضافه شد.${createdText}`);

      await Promise.all([fetchNetworks(), fetchNetworkDetail(networkId)]);
    });
  };

  const handleEndMembership = (membershipId) => {
    const membership = parseId(membershipId);
    if (!membership) return;

    runAction(async () => {
      const response = await authFetch(
        `${PARTICIPATION_BASE_PATH}/networks/memberships/${membership}/end/`,
        {
          method: "POST",
        },
      );

      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(payload, "پایان عضویت با خطا مواجه شد."),
        );
        return;
      }

      setActionSuccess("عضویت با موفقیت پایان یافت.");
      await Promise.all([
        fetchNetworks(),
        fetchNetworkDetail(selectedNetworkId || assignNetworkId),
      ]);
    });
  };

  const handleRemoveNetwork = (networkId) => {
    const id = parseId(networkId);
    if (!id) return;

    runAction(async () => {
      const response = await authFetch(`${NETWORKS_ENDPOINT}${id}/remove/`, {
        method: "POST",
      });

      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(payload, "حذف شبکه با خطا مواجه شد."),
        );
        return;
      }

      setActionSuccess("شبکه با موفقیت حذف شد.");
      if (String(selectedNetworkId) === String(id)) {
        setSelectedNetworkId("");
      }
      if (String(assignNetworkId) === String(id)) {
        setAssignNetworkId("");
      }

      await fetchNetworks();
    });
  };

  const networksTableData = useMemo(() => {
    return networks.map((network) => ({
      idRaw: network.id,
      id: network.id ?? "-",
      name: network.name ?? "-",
      code: network.code ?? "-",
      status: network.is_active ? "فعال" : "غیرفعال",
      owner: network.owner_name ?? "-",
      sale: formatTradePrice(network.entire_sale_usd),
      soldCount: formatCount(network.entire_number_of_challenges_sold),
      totalProfit: formatTradePrice(network.total_profit_participants_made),
      joinedUsers: formatCount(network.total_users_joined_via_network),
    }));
  }, [networks]);

  const selectedNetwork = selectedNetworkDetail?.network || null;
  const selectedOwner = selectedNetworkDetail?.owner || null;
  const selectedBloggers = Array.isArray(selectedNetworkDetail?.bloggers)
    ? selectedNetworkDetail.bloggers
    : [];

  return (
    <div className="w-full min-h-0 flex flex-col gap-2.5">
      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          تعریف شبکه و اعضا
        </p>

        {pageError && <p className="text-red-500 text-sm">{pageError}</p>}
        {actionError && <p className="text-red-500 text-sm">{actionError}</p>}
        {actionSuccess && (
          <p className="text-green-500 text-sm">{actionSuccess}</p>
        )}

        <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-5">
          <div className="rounded-xl border border-zi-border-light dark:border-zi-border-dark p-4 flex flex-col gap-3">
            <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark">
              مدیریت پروفایل نقش‌ها
            </p>

            <SelectField
              label="کاربر بلاگر"
              value={bloggerUserId}
              onChange={(event) => setBloggerUserId(event.target.value)}
              options={userOptions}
              disabled={usersLoading || actionLoading}
            />

            <Pagination
              page={usersPagination.page}
              totalPages={usersPagination.totalPages}
              loading={usersLoading}
              onPageChange={fetchUsers}
            />

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleCreateProfile("blogger")}
                disabled={actionLoading}
                className="rounded-lg py-2 text-sm text-zi-text-dark bg-zi-gradient-button-blue disabled:opacity-60"
              >
                {actionLoading ? <ButtonLoader /> : "ایجاد پروفایل بلاگر"}
              </button>
              <button
                type="button"
                onClick={() => handleDeleteProfile("blogger")}
                disabled={actionLoading}
                className="rounded-lg py-2 text-sm text-red-500 border border-red-500/50 disabled:opacity-60"
              >
                {actionLoading ? <ButtonLoader /> : "حذف پروفایل بلاگر"}
              </button>
            </div>

            <SelectField
              label="کاربر مالک شبکه"
              value={ownerUserId}
              onChange={(event) => setOwnerUserId(event.target.value)}
              options={userOptions}
              disabled={usersLoading || actionLoading}
            />

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleCreateProfile("network_owner")}
                disabled={actionLoading}
                className="rounded-lg py-2 text-sm text-zi-text-dark bg-zi-gradient-button-blue disabled:opacity-60"
              >
                {actionLoading ? <ButtonLoader /> : "ایجاد پروفایل مالک شبکه"}
              </button>
              <button
                type="button"
                onClick={() => handleDeleteProfile("network_owner")}
                disabled={actionLoading}
                className="rounded-lg py-2 text-sm text-red-500 border border-red-500/50 disabled:opacity-60"
              >
                {actionLoading ? <ButtonLoader /> : "حذف پروفایل مالک شبکه"}
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-zi-border-light dark:border-zi-border-dark p-4 flex flex-col gap-3">
            <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark">
              ایجاد شبکه و افزودن عضو
            </p>

            <SelectField
              label="مالک شبکه"
              value={networkOwnerUserId}
              onChange={(event) => setNetworkOwnerUserId(event.target.value)}
              options={userOptions}
              disabled={usersLoading || actionLoading}
            />

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2">
              <TextField
                label="نام شبکه"
                value={networkName}
                onChange={(event) => setNetworkName(event.target.value)}
                placeholder="مثلا Network A"
              />
              <TextField
                label="کد شبکه"
                value={networkCode}
                onChange={(event) => setNetworkCode(event.target.value)}
                placeholder="مثلا NTA"
              />
            </div>

            <button
              type="button"
              onClick={handleCreateNetwork}
              disabled={actionLoading}
              className="rounded-lg py-2 text-sm text-zi-text-dark bg-zi-gradient-button-blue disabled:opacity-60"
            >
              {actionLoading ? <ButtonLoader /> : "ایجاد شبکه"}
            </button>

            <div className="w-full border-t border-zi-border-light dark:border-zi-border-dark pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <SelectField
                label="شبکه"
                value={assignNetworkId}
                onChange={(event) => setAssignNetworkId(event.target.value)}
                options={networkOptions}
                disabled={networksLoading || actionLoading}
              />
              <SelectField
                label="کاربر"
                value={assignUserId}
                onChange={(event) => setAssignUserId(event.target.value)}
                options={userOptions}
                disabled={usersLoading || actionLoading}
              />
            </div>

            <button
              type="button"
              onClick={handleAssignBlogger}
              disabled={actionLoading}
              className="rounded-lg py-2 text-sm text-zi-text-dark bg-zi-gradient-button-blue disabled:opacity-60"
            >
              {actionLoading ? <ButtonLoader /> : "افزودن عضو به شبکه"}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <div className="w-full flex items-center justify-between gap-3">
          <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
            لیست شبکه‌ها
          </p>
        </div>

        <Table
          columns={[
            { key: "id", label: "شناسه" },
            { key: "name", label: "نام شبکه" },
            { key: "code", label: "کد" },
            { key: "status", label: "وضعیت" },
            { key: "owner", label: "مالک" },
            { key: "sale", label: "فروش کل" },
            { key: "soldCount", label: "تعداد فروش" },
            { key: "totalProfit", label: "سود کل مشارکت‌کنندگان" },
            { key: "joinedUsers", label: "کاربران جذب‌شده" },
            {
              key: "detail",
              label: "عملیات",
              render: (_, row) => (
                <div className="w-full flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedNetworkId(String(row.idRaw))}
                    className="rounded-md px-2 py-1 text-xs border border-zi-blue-light text-zi-blue-light"
                  >
                    جزئیات
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveNetwork(row.idRaw)}
                    disabled={actionLoading}
                    className="rounded-md px-2 py-1 text-xs border border-red-500/50 text-red-500 disabled:opacity-60"
                  >
                    {actionLoading ? <ButtonLoader /> : "حذف شبکه"}
                  </button>
                </div>
              ),
            },
          ]}
          data={networksTableData}
          loading={networksLoading && networksPagination.page === 1}
          error={pageError}
        />
        <Pagination
          page={networksPagination.page}
          totalPages={networksPagination.totalPages}
          loading={networksLoading}
          onPageChange={fetchNetworks}
        />
      </div>

      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          جزئیات شبکه انتخاب‌شده
        </p>

        {detailLoading && (
          <div role="status" aria-label="در حال بارگذاری جزئیات شبکه" className="flex min-h-32 items-center justify-center text-[#8080804d]"><Spinner /></div>
        )}

        {!detailLoading && !selectedNetwork && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            برای مشاهده جزئیات، یک شبکه را انتخاب کنید.
          </p>
        )}

        {!detailLoading && selectedNetwork && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5">
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  شبکه
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {selectedNetwork.name || "-"} ({selectedNetwork.code || "-"})
                </p>
              </div>
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  مالک
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {selectedOwner?.name || "-"}
                </p>
              </div>
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  فروش کل
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {formatTradePrice(selectedNetwork.entire_sale_usd)}
                </p>
              </div>
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  چالش‌های فروخته‌شده
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {formatCount(
                    selectedNetwork.entire_number_of_challenges_sold,
                  )}
                </p>
              </div>
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3 md:col-span-2">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  فروش به تفکیک پکیج
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {formatPackageBreakdown(
                    selectedNetwork.entire_number_of_challenges_by_package,
                  )}
                </p>
              </div>
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  سود کل مشارکت‌کنندگان
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {formatTradePrice(
                    selectedNetwork.total_profit_participants_made,
                  )}
                </p>
              </div>
              <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
                <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
                  کاربران جذب‌شده
                </p>
                <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark">
                  {formatCount(selectedNetwork.total_users_joined_via_network)}
                </p>
              </div>
            </div>

            <div className="w-full flex flex-col gap-2.5">
              <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark">
                اعضای شبکه (بلاگرها)
              </p>
              <Table
                columns={[
                  { key: "userId", label: "شناسه کاربر" },
                  { key: "name", label: "نام" },
                  { key: "email", label: "ایمیل" },
                  { key: "membershipId", label: "شناسه عضویت" },
                  { key: "membershipStarted", label: "شروع عضویت" },
                  { key: "membershipEnded", label: "پایان عضویت" },
                  { key: "sold", label: "تعداد فروش" },
                  { key: "profitMade", label: "سود ساخته‌شده" },
                  { key: "profitPaid", label: "سود پرداخت‌شده" },
                  { key: "profitCanPayout", label: "قابل برداشت" },
                  {
                    key: "action",
                    label: "عملیات",
                    render: (_, row) => {
                      if (!row.membershipIdRaw) return "-";
                      if (row.membershipEndedRaw) return "پایان یافته";

                      return (
                        <button
                          type="button"
                          onClick={() =>
                            handleEndMembership(row.membershipIdRaw)
                          }
                          disabled={actionLoading}
                          className="rounded-md px-2 py-1 text-xs border border-red-500/50 text-red-500 disabled:opacity-60"
                        >
                          {actionLoading ? <ButtonLoader /> : "پایان عضویت"}
                        </button>
                      );
                    },
                  },
                ]}
                data={selectedBloggers.map((blogger) => ({
                  membershipIdRaw: blogger.membership_id,
                  membershipEndedRaw: blogger.membership_ended_at,
                  userId: blogger.user_id ?? "-",
                  name: blogger.name ?? "-",
                  email: blogger.email ?? "-",
                  membershipId: blogger.membership_id ?? "-",
                  membershipStarted: formatDate(blogger.membership_started_at),
                  membershipEnded: formatDate(blogger.membership_ended_at),
                  sold: formatCount(blogger.entire_challenges_count_sold),
                  profitMade: formatTradePrice(blogger.profit_made),
                  profitPaid: formatTradePrice(blogger.profit_payed_out),
                  profitCanPayout: formatTradePrice(blogger.profit_can_payout),
                }))}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
